/**
 * WriteProvider — Abstract base class for write operations.
 *
 * Only available with the blockchain data source. Archive and wizard
 * providers return null for the write provider.
 *
 * All write methods accept a `callbacks` object for UI feedback:
 *   { onLoading, onSuccess, onError }
 */
class WriteProvider {
  constructor() {
    if (new.target === WriteProvider) {
      throw new Error('WriteProvider is abstract and cannot be instantiated directly');
    }
  }

  // --- Anyone functions ---

  /**
   * Create a new project on-chain.
   * @param {string} projectName
   * @param {string} artistName
   * @param {string} projectDescription
   * @param {string} pricePerTokenInEth
   * @param {number} maxEditions
   * @param {string} scriptTransactionHash
   * @param {string} scriptType
   * @param {Object} callbacks — {onLoading, onSuccess, onError}
   */
  async createNewProject(projectName, artistName, projectDescription, pricePerTokenInEth, maxEditions, scriptTransactionHash, scriptType, callbacks) {
    throw new Error('createNewProject() must be implemented');
  }

  /**
   * Mint an edition of a project.
   * @param {number} projectId
   * @param {string} priceInWei
   * @param {Object} callbacks — {onLoading, onSuccess, onError}
   */
  async mint(projectId, priceInWei, callbacks) {
    throw new Error('mint() must be implemented');
  }

  // --- Admin functions ---

  async contractOwnerUpdateWebsiteUrl(newUrl, callbacks) {
    throw new Error('contractOwnerUpdateWebsiteUrl() must be implemented');
  }

  async contractOwnerUpdateAPIURL(newUrl, callbacks) {
    throw new Error('contractOwnerUpdateAPIURL() must be implemented');
  }

  async contractOwnerUpdateUseMetadataServer(decision, callbacks) {
    throw new Error('contractOwnerUpdateUseMetadataServer() must be implemented');
  }

  async contractOwnerUpdateGlobalSecondaryRoyaltyPercent(newRoyaltyPercent, callbacks) {
    throw new Error('contractOwnerUpdateGlobalSecondaryRoyaltyPercent() must be implemented');
  }

  async contractOwnerUpdateProjectFee(newProjectFeeInEth, callbacks) {
    throw new Error('contractOwnerUpdateProjectFee() must be implemented');
  }

  async contractOwnerUpdateArtistPercent(newPercent, callbacks) {
    throw new Error('contractOwnerUpdateArtistPercent() must be implemented');
  }

  async contractOwnerAddTeammember(newTeammember, callbacks) {
    throw new Error('contractOwnerAddTeammember() must be implemented');
  }

  async contractOwnerRemoveTeammember(delTeammember, callbacks) {
    throw new Error('contractOwnerRemoveTeammember() must be implemented');
  }

  async teamToggleArtistScreeningEnabled(callbacks) {
    throw new Error('teamToggleArtistScreeningEnabled() must be implemented');
  }

  async teamAddAuthorizedArtist(newArtist, callbacks) {
    throw new Error('teamAddAuthorizedArtist() must be implemented');
  }

  async teamRemoveAuthorizedArtist(delArtist, callbacks) {
    throw new Error('teamRemoveAuthorizedArtist() must be implemented');
  }

  async contractOwnerUpdateCuratorAddressAndPercent(curator, percent, callbacks) {
    throw new Error('contractOwnerUpdateCuratorAddressAndPercent() must be implemented');
  }

  async contractOwnerUpdateBeneficiaryAddressAndPercent(newBeneficiary, newPercent, callbacks) {
    throw new Error('contractOwnerUpdateBeneficiaryAddressAndPercent() must be implemented');
  }

  async adminwithdraw(callbacks) {
    throw new Error('adminwithdraw() must be implemented');
  }

  async releaseRoyaltiesForProject(projectId, callbacks) {
    throw new Error('releaseRoyaltiesForProject() must be implemented');
  }

  // --- Project admin functions ---

  async artistTeamUpdateProjectName(projectId, newProjectName, callbacks) {
    throw new Error('artistTeamUpdateProjectName() must be implemented');
  }

  async artistTeamUpdateProjectDescription(projectId, newProjectDescription, callbacks) {
    throw new Error('artistTeamUpdateProjectDescription() must be implemented');
  }

  async artistTeamUpdateArtistName(projectId, newArtistName, callbacks) {
    throw new Error('artistTeamUpdateArtistName() must be implemented');
  }

  async artistTeamUpdateProjectPricePerToken(projectId, newPriceInEth, callbacks) {
    throw new Error('artistTeamUpdateProjectPricePerToken() must be implemented');
  }

  async artistTeamUpdateProjectMaxEditions(projectId, newMaxEditions, callbacks) {
    throw new Error('artistTeamUpdateProjectMaxEditions() must be implemented');
  }

  async artistTeamUpdateProjectMaxGridDimension(projectId, newMaxGridDimension, callbacks) {
    throw new Error('artistTeamUpdateProjectMaxGridDimension() must be implemented');
  }

  async teamToggleProjectIsActive(projectId, callbacks) {
    throw new Error('teamToggleProjectIsActive() must be implemented');
  }

  async artistTeamToggleProjectIsPaused(projectId, callbacks) {
    throw new Error('artistTeamToggleProjectIsPaused() must be implemented');
  }

  async artistTeamUpdateProjectImageURLInfo(projectId, newIPFSCID, newIPFSCIDExt, useImageURLInGridView, callbacks) {
    throw new Error('artistTeamUpdateProjectImageURLInfo() must be implemented');
  }

  async artistTeamLockProjectContractOwnerUnlockProject(projectId, newState, callbacks) {
    throw new Error('artistTeamLockProjectContractOwnerUnlockProject() must be implemented');
  }

  async teamUpdateProjectCategory(projectId, category, callbacks) {
    throw new Error('teamUpdateProjectCategory() must be implemented');
  }

  async artistTeamUpdateProjectScriptTransactionHash(projectId, newTransactionHash, callbacks) {
    throw new Error('artistTeamUpdateProjectScriptTransactionHash() must be implemented');
  }

  async artistTeamUpdateProjectScriptType(projectId, newScriptType, callbacks) {
    throw new Error('artistTeamUpdateProjectScriptType() must be implemented');
  }

  async teamUpdateFeaturedProject(projectId, callbacks) {
    throw new Error('teamUpdateFeaturedProject() must be implemented');
  }

  async artistUpdateProjectAdditionalPayeeInfo(projectId, beneficiary, percent, callbacks) {
    throw new Error('artistUpdateProjectAdditionalPayeeInfo() must be implemented');
  }

  async artistUpdateProjectArtistAddress(projectId, newAddress, callbacks) {
    throw new Error('artistUpdateProjectArtistAddress() must be implemented');
  }

  async artistOwnerUpdateTokenWithMessage(tokenId, message, callbacks) {
    throw new Error('artistOwnerUpdateTokenWithMessage() must be implemented');
  }
}

export default WriteProvider;
